/**
 * @fileoverview Google Ads OAuth API service.
 * @module features/settings/api/googleAdsOAuthService
 *
 * NOTE: OAuth endpoints call api.abyssblock.com directly (not through proxy).
 * This ensures session cookies are set for the same domain as the OAuth callback.
 */

import axios from 'axios'
import { z } from 'zod'
import apiClient, { resolveAuthToken, readActiveOrgIdFromStorage } from '@/lib/apiClient'

/**
 * Direct API client for OAuth endpoints.
 * Production: https://api.abyssblock.com/backend/api
 * Local dev: http://localhost:8080/api (no /backend prefix locally)
 */
const OAUTH_API_BASE =
  import.meta.env.VITE_OAUTH_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:8080/api' : 'https://api.abyssblock.com/backend/api')

const oauthClient = axios.create({
  baseURL: OAUTH_API_BASE,
  withCredentials: true,
})

// Add JWT auth interceptor (same as apiClient) so protected endpoints work
oauthClient.interceptors.request.use(
  async config => {
    const token = await resolveAuthToken()
    if (token) {
      ;(config.headers as Record<string, unknown>).Authorization = `Bearer ${token}`
    }
    const orgId = readActiveOrgIdFromStorage()
    if (orgId) {
      ;(config.headers as Record<string, unknown>)['X-Org-Id'] = orgId
    }
    return config
  },
  error => Promise.reject(error)
)

/**
 * Schema for OAuth configuration response.
 */
export const GoogleAdsOAuthConfigSchema = z.object({
  clientId: z.string(),
  redirectUri: z.string(),
  scope: z.string(),
  configured: z.boolean(),
})

export type GoogleAdsOAuthConfig = z.infer<typeof GoogleAdsOAuthConfigSchema>

/**
 * Schema for authorization URL response.
 */
export const AuthorizeResponseSchema = z.object({
  authorizationUrl: z.string(),
  state: z.string(),
})

export type AuthorizeResponse = z.infer<typeof AuthorizeResponseSchema>

/**
 * Schema for callback response.
 */
export const CallbackResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().nullable(),
  redirectUrl: z.string().nullable(),
})

export type CallbackResponse = z.infer<typeof CallbackResponseSchema>

/**
 * Schema for customer account info.
 */
export const CustomerAccountInfoSchema = z.object({
  customerId: z.string(),
  resourceName: z.string(),
  descriptiveName: z.string().nullable(),
  isManager: z.boolean(),
})

export type CustomerAccountInfo = z.infer<typeof CustomerAccountInfoSchema>

/**
 * Schema for accounts response.
 */
export const AccountsResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().nullable(),
  accounts: z.array(CustomerAccountInfoSchema),
})

export type AccountsResponse = z.infer<typeof AccountsResponseSchema>

/**
 * Schema for complete OAuth request.
 */
export const CompleteRequestSchema = z.object({
  customerId: z.string().min(1, 'Customer ID is required'),
  accountName: z.string().nullable(),
  isManager: z.boolean(),
})

/**
 * Schema for complete OAuth response.
 */
export const CompleteResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().nullable(),
})

export type CompleteResponse = z.infer<typeof CompleteResponseSchema>

/**
 * Fetches Google Ads OAuth configuration.
 * Uses proxied apiClient (no session needed).
 */
export async function getGoogleAdsOAuthConfig(): Promise<GoogleAdsOAuthConfig> {
  const res = await apiClient.get('/oauth/google-ads/config')
  return GoogleAdsOAuthConfigSchema.parse(res.data)
}

/**
 * Gets the authorization URL to initiate OAuth flow.
 * Uses direct oauthClient to ensure session cookie is set for correct domain.
 */
export async function getGoogleAdsAuthorizationUrl(): Promise<AuthorizeResponse> {
  const res = await oauthClient.get('/oauth/google-ads/authorize')
  return AuthorizeResponseSchema.parse(res.data)
}

/**
 * Handles OAuth callback by exchanging code for tokens.
 * Uses direct oauthClient for session continuity.
 */
export async function handleGoogleAdsCallback(
  code: string,
  state: string
): Promise<CallbackResponse> {
  const res = await oauthClient.post('/oauth/google-ads/callback', { code, state })
  return CallbackResponseSchema.parse(res.data)
}

/**
 * Gets list of accessible Google Ads customer accounts.
 * Uses direct oauthClient for session continuity.
 */
export async function getGoogleAdsAccounts(): Promise<AccountsResponse> {
  const res = await oauthClient.get('/oauth/google-ads/accounts')
  return AccountsResponseSchema.parse(res.data)
}

/**
 * Completes OAuth flow with selected customer account.
 * Uses direct oauthClient for session continuity.
 */
export async function completeGoogleAdsOAuth(
  account: CustomerAccountInfo
): Promise<CompleteResponse> {
  const res = await oauthClient.post('/oauth/google-ads/complete', {
    customerId: account.customerId,
    accountName: account.descriptiveName,
    isManager: account.isManager,
  })
  return CompleteResponseSchema.parse(res.data)
}

/**
 * Disconnects Google Ads integration by revoking credentials.
 */
export async function disconnectGoogleAds(): Promise<void> {
  await apiClient.delete('/oauth/google-ads/disconnect')
}
